import { useEffect, useMemo, useState } from "react";
import "../../../assets/css/admin/permission/PermissionAdmin.css";
import "../../../assets/css/admin/AdminCommon.css";
import flowerIcon from "../../../assets/img/flower_icon.png";
import {
  createAdmin,
  deleteAdmin,
  getAdminList,
  getCollegeList,
  resetAdminPassword,
  updateAdmin,
  updateCollegeName,
} from "../../../api/permissionApi";

const ADMINS_PER_PAGE = 5;
const PAGE_BUTTONS_PER_GROUP = 5;
const COUNCIL_NAMES_STORAGE_KEY = "councilNames";

const permissionGroupOptions = [
  {
    value: "총학생회 관리자",
    label: "총학생회 관리자",
  },
  {
    value: "감사위원회 관리자",
    label: "감사위원회 관리자",
  },
  {
    value: "동아리연합회 관리자",
    label: "동아리연합회 관리자",
  },
  {
    value: "아트앤디자인대학 관리자",
    label: "아트앤디자인대학 관리자",
  },
  {
    value: "약학대학 관리자",
    label: "약학대학 관리자",
  },
  {
    value: "미래인재대학 관리자",
    label: "미래인재대학 관리자",
  },
  {
    value: "글로벌융합대학 관리자",
    label: "글로벌융합대학 관리자",
  },
  {
    value: "과학기술대학 관리자",
    label: "과학기술대학 관리자",
  },
];

const USER_VISIBLE_COLLEGE_IDS = [1, 2, 3, 4, 5, 6];

const pathMap = {
  1: "Scitech",
  2: "Global",
  3: "Future",
  4: "Pharm",
  5: "ArtDesign",
  6: "Club",
  7: "Audit",
};

const normalizeAdmin = (item) => ({
  id: item.userId || item.id,
  userId: item.userId || item.id,
  loginId: item.loginId || "",
  name: item.userName || item.name || "",
  roleName: item.roleDisplay || item.roleName || "",
});

const normalizeCollege = (item) => {
  const collegeId = item.collegeId || item.id;
  const collegeName = item.collegeName || item.name || "";

  return {
    id: collegeId,
    collegeId,
    name: collegeName,
    currentName: collegeName,
    editName: collegeName,
    shortName: collegeName.replace(" 학생회", ""),
    path: pathMap[collegeId] || "",
    logoUrl: item.logoUrl || "",
  };
};

const getPagedList = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.content)) return result.content;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.list)) return result.list;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.data?.content)) return result.data.content;

  return [];
};

function PermissionAdmin() {
  const [activeTab, setActiveTab] = useState("admin");

  const [admins, setAdmins] = useState([]);
  const [councilNames, setCouncilNames] = useState([]);

  const [newAdmin, setNewAdmin] = useState({
    loginId: "",
    name: "",
    password: "",
    roleGroup: "",
  });

  const [editingAdmin, setEditingAdmin] = useState(null);
  const [passwordResetMessage, setPasswordResetMessage] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [isCollegeLoading, setIsCollegeLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const totalPages = Math.max(1, Math.ceil(admins.length / ADMINS_PER_PAGE));

  const currentAdmins = useMemo(() => {
    const startIndex = (currentPage - 1) * ADMINS_PER_PAGE;
    const endIndex = startIndex + ADMINS_PER_PAGE;

    return admins.slice(startIndex, endIndex);
  }, [admins, currentPage]);

  const visiblePageNumbers = useMemo(() => {
    const groupStart =
      Math.floor((currentPage - 1) / PAGE_BUTTONS_PER_GROUP) *
        PAGE_BUTTONS_PER_GROUP +
      1;

    const groupEnd = Math.min(
      groupStart + PAGE_BUTTONS_PER_GROUP - 1,
      totalPages
    );

    return Array.from(
      {
        length: groupEnd - groupStart + 1,
      },
      (_, index) => groupStart + index
    );
  }, [currentPage, totalPages]);

  const filteredCouncilNames = useMemo(() => {
    return councilNames.filter((item) =>
      USER_VISIBLE_COLLEGE_IDS.includes(Number(item.collegeId || item.id))
    );
  }, [councilNames]);

  const fetchAdmins = async () => {
    try {
      setIsAdminLoading(true);

      const result = await getAdminList({ page: 0, size: 100 });
      const list = getPagedList(result).map(normalizeAdmin);

      setAdmins(list);
    } catch (error) {
      console.error("관리자 목록 조회 실패:", error);
      alert(error.message || "관리자 목록 조회에 실패했습니다.");
    } finally {
      setIsAdminLoading(false);
    }
  };

  const fetchColleges = async () => {
    try {
      setIsCollegeLoading(true);

      const result = await getCollegeList();
      const formattedList = result.map(normalizeCollege);

      const userVisibleList = formattedList.filter((item) =>
        USER_VISIBLE_COLLEGE_IDS.includes(Number(item.collegeId || item.id))
      );

      setCouncilNames(formattedList);

      localStorage.setItem(
        COUNCIL_NAMES_STORAGE_KEY,
        JSON.stringify(userVisibleList)
      );
    } catch (error) {
      console.error("중운위 목록 조회 실패:", error);
      alert(error.message || "중운위 목록 조회에 실패했습니다.");
    } finally {
      setIsCollegeLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
    fetchColleges();
  }, []);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleNewAdminChange = (key, value) => {
    setNewAdmin((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleAddAdmin = async () => {
    if (!newAdmin.loginId.trim()) {
      alert("아이디를 입력해 주세요.");
      return;
    }

    if (!newAdmin.name.trim()) {
      alert("이름을 입력해 주세요.");
      return;
    }

    if (!newAdmin.password.trim()) {
      alert("초기 비밀번호를 입력해 주세요.");
      return;
    }

    if (!newAdmin.roleGroup) {
      alert("권한 그룹을 선택해 주세요.");
      return;
    }

    const isDuplicateLoginId = admins.some(
      (admin) => admin.loginId === newAdmin.loginId.trim()
    );

    if (isDuplicateLoginId) {
      alert("이미 사용 중인 아이디입니다.");
      return;
    }

    try {
      setIsSaving(true);

      const createdAdmin = await createAdmin({
        loginId: newAdmin.loginId.trim(),
        userName: newAdmin.name.trim(),
        password: newAdmin.password.trim(),
        roleGroup: newAdmin.roleGroup,
      });

      const normalizedAdmin = normalizeAdmin(createdAdmin);

      setAdmins((prev) => {
        const nextAdmins = [normalizedAdmin, ...prev];
        return nextAdmins;
      });

      setCurrentPage(1);

      setNewAdmin({
        loginId: "",
        name: "",
        password: "",
        roleGroup: "",
      });

      alert("새 관리자가 추가되었습니다.");
    } catch (error) {
      console.error("관리자 추가 실패:", error);
      alert(error.message || "관리자 추가에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAdmin = async (id) => {
    const confirmDelete = window.confirm("관리자를 삭제하시겠습니까?");

    if (!confirmDelete) {
      return;
    }

    try {
      setIsSaving(true);

      await deleteAdmin(id);

      setAdmins((prev) => prev.filter((admin) => admin.id !== id));

      alert("관리자가 삭제되었습니다.");
    } catch (error) {
      console.error("관리자 삭제 실패:", error);
      alert(error.message || "관리자 삭제에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin({
      ...admin,
    });

    setPasswordResetMessage("");
  };

  const handleEditAdminChange = (key, value) => {
    setEditingAdmin((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const handleCloseEditModal = () => {
    setEditingAdmin(null);
    setPasswordResetMessage("");
  };

  const handleResetPassword = async () => {
    if (!editingAdmin?.id) {
      alert("초기화할 관리자를 찾을 수 없습니다.");
      return;
    }

    const confirmReset = window.confirm(
      `${editingAdmin.name} 관리자의 비밀번호를 초기화하시겠습니까?`
    );

    if (!confirmReset) {
      return;
    }

    try {
      setIsSaving(true);

      await resetAdminPassword(editingAdmin.id);

      setPasswordResetMessage(editingAdmin.loginId);

      alert(
        `비밀번호가 초기화되었습니다. 초기화된 비밀번호는 아이디와 동일합니다.\n초기화된 비밀번호: ${editingAdmin.loginId}`
      );
    } catch (error) {
      console.error("비밀번호 초기화 실패:", error);
      alert(error.message || "비밀번호 초기화에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEditedAdmin = async () => {
    if (!editingAdmin) {
      return;
    }

    if (!editingAdmin.name.trim()) {
      alert("이름을 입력해 주세요.");
      return;
    }

    if (!editingAdmin.roleName) {
      alert("권한 그룹을 선택해 주세요.");
      return;
    }

    try {
      setIsSaving(true);

      const updatedAdmin = await updateAdmin(editingAdmin.id, {
        loginId: editingAdmin.loginId,
        userName: editingAdmin.name.trim(),
        roleGroup: editingAdmin.roleName,
      });

      const normalizedAdmin = normalizeAdmin(updatedAdmin);

      setAdmins((prev) =>
        prev.map((admin) =>
          admin.id === editingAdmin.id ? normalizedAdmin : admin
        )
      );

      handleCloseEditModal();
      alert("관리자 정보가 수정되었습니다.");
    } catch (error) {
      console.error("관리자 수정 실패:", error);
      alert(error.message || "관리자 정보 수정에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCouncilNameChange = (id, value) => {
    setCouncilNames((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              editName: value,
            }
          : item
      )
    );
  };

  const handleCancelCouncilName = (id) => {
    setCouncilNames((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              editName: item.currentName,
            }
          : item
      )
    );
  };

  const handleSaveCouncilName = async (id) => {
    const targetCouncil = councilNames.find((item) => item.id === id);

    if (!targetCouncil) {
      alert("수정할 중운위 정보를 찾을 수 없습니다.");
      return;
    }

    if (!targetCouncil.editName.trim()) {
      alert("중운위 이름을 입력해 주세요.");
      return;
    }

    try {
      setIsSaving(true);

      const updatedCollege = await updateCollegeName(
        targetCouncil.collegeId || targetCouncil.id,
        targetCouncil.editName.trim()
      );

      const updatedName =
        updatedCollege?.collegeName || targetCouncil.editName.trim();

      setCouncilNames((prev) => {
        const updatedCouncilNames = prev.map((item) =>
          item.id === id
            ? {
                ...item,
                name: updatedName,
                currentName: updatedName,
                editName: updatedName,
                shortName: updatedName.replace(" 학생회", ""),
              }
            : item
        );

        const userVisibleList = updatedCouncilNames.filter((item) =>
          USER_VISIBLE_COLLEGE_IDS.includes(Number(item.collegeId || item.id))
        );

        localStorage.setItem(
          COUNCIL_NAMES_STORAGE_KEY,
          JSON.stringify(userVisibleList)
        );

        return updatedCouncilNames;
      });

      alert("중운위 이름이 저장되었습니다.");
    } catch (error) {
      console.error("중운위 이름 저장 실패:", error);
      alert(error.message || "중운위 이름 저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAll = () => {
    alert("관리자 정보는 추가/수정/삭제 시 바로 저장됩니다.");
  };

  return (
    <div className="permission-page admin-scroll-x">
      <div className="permission-inner">
        <h2 className="permission-title">권한 관리</h2>

        <div className="permission-top-tabs">
          <button
            type="button"
            className={activeTab === "admin" ? "active" : ""}
            onClick={() => setActiveTab("admin")}
          >
            관리자 추가/관리
          </button>

          <button
            type="button"
            className={activeTab === "name" ? "active" : ""}
            onClick={() => setActiveTab("name")}
          >
            중운위 이름 관리
          </button>
        </div>

        {activeTab === "admin" && (
          <>
            <section className="permission-section">
              <h3 className="permission-section-title">새 관리자 추가</h3>

              <div className="permission-form">
                <PermissionInputRow label="아이디">
                  <input
                    type="text"
                    placeholder="아이디를 입력해 주세요."
                    value={newAdmin.loginId}
                    onChange={(e) =>
                      handleNewAdminChange("loginId", e.target.value)
                    }
                  />
                </PermissionInputRow>

                <PermissionInputRow label="이름">
                  <input
                    type="text"
                    placeholder="이름을 입력해 주세요."
                    value={newAdmin.name}
                    onChange={(e) =>
                      handleNewAdminChange("name", e.target.value)
                    }
                  />
                </PermissionInputRow>

                <PermissionInputRow label="초기 비밀번호">
                  <input
                    type="password"
                    placeholder="초기 비밀번호를 입력하세요."
                    value={newAdmin.password}
                    onChange={(e) =>
                      handleNewAdminChange("password", e.target.value)
                    }
                  />
                </PermissionInputRow>

                <PermissionInputRow label="권한 그룹">
                  <select
                    value={newAdmin.roleGroup}
                    onChange={(e) =>
                      handleNewAdminChange("roleGroup", e.target.value)
                    }
                  >
                    <option value="">권한 그룹을 선택해 주세요.</option>

                    {permissionGroupOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </PermissionInputRow>

                <div className="permission-add-row">
                  <button
                    type="button"
                    className="permission-small-pink-btn"
                    onClick={handleAddAdmin}
                    disabled={isSaving}
                  >
                    추가
                  </button>
                </div>
              </div>
            </section>

            <section className="permission-section permission-list-section admin-scroll-x">
              <h3 className="permission-section-title">관리자 목록</h3>

              <div className="permission-table">
                <div className="permission-table-head">
                  <div>아이디</div>
                  <div>이름</div>
                  <div>부여된 권한</div>
                  <div>관리</div>
                </div>

                {isAdminLoading ? (
                  <div className="permission-table-empty">
                    관리자 목록을 불러오는 중입니다.
                  </div>
                ) : currentAdmins.length > 0 ? (
                  currentAdmins.map((admin) => (
                    <div className="permission-table-row" key={admin.id}>
                      <div>{admin.loginId}</div>
                      <div>{admin.name}</div>
                      <div>{admin.roleName}</div>

                      <div className="permission-action-cell">
                        <button
                          type="button"
                          onClick={() => handleEditAdmin(admin)}
                          disabled={isSaving}
                        >
                          수정
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteAdmin(admin.id)}
                          disabled={isSaving}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="permission-table-empty">
                    등록된 관리자가 없습니다.
                  </div>
                )}
              </div>

              {admins.length > 0 && (
                <div className="permission-pagination">
                  <button
                    type="button"
                    aria-label="첫 페이지"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    ‹‹
                  </button>

                  <button
                    type="button"
                    aria-label="이전 페이지"
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                  >
                    ‹
                  </button>

                  {visiblePageNumbers.map((page) => (
                    <button
                      type="button"
                      key={page}
                      className={currentPage === page ? "active" : ""}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    type="button"
                    aria-label="다음 페이지"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(prev + 1, totalPages)
                      )
                    }
                  >
                    ›
                  </button>

                  <button
                    type="button"
                    aria-label="마지막 페이지"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    ››
                  </button>
                </div>
              )}
            </section>

            <div className="permission-bottom-save">
              <button type="button" onClick={handleSaveAll}>
                저장
              </button>
            </div>
          </>
        )}

        {activeTab === "name" && (
          <>
            <section className="permission-name-header">
              <h3>중앙운영위원회 이름 관리</h3>
            </section>

            <section className="permission-name-card admin-scroll-x">
              <div className="permission-name-table">
                <div className="permission-name-head">
                  <div>현재 이름</div>
                  <div>수정할 이름</div>
                  <div>관리</div>
                </div>

                {isCollegeLoading ? (
                  <div className="permission-table-empty">
                    중운위 목록을 불러오는 중입니다.
                  </div>
                ) : filteredCouncilNames.length > 0 ? (
                  filteredCouncilNames.map((item) => (
                    <div className="permission-name-row" key={item.id}>
                      <div className="permission-current-name">
                        {item.currentName}
                      </div>

                      <div>
                        <input
                          type="text"
                          value={item.editName}
                          onChange={(e) =>
                            handleCouncilNameChange(item.id, e.target.value)
                          }
                        />
                      </div>

                      <div className="permission-name-actions">
                        <button
                          type="button"
                          className="permission-outline-btn"
                          onClick={() => handleCancelCouncilName(item.id)}
                          disabled={isSaving}
                        >
                          취소
                        </button>

                        <button
                          type="button"
                          className="permission-pink-btn"
                          onClick={() => handleSaveCouncilName(item.id)}
                          disabled={isSaving}
                        >
                          저장
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="permission-table-empty">
                    등록된 중운위가 없습니다.
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {editingAdmin && (
        <div
          className="permission-modal-backdrop admin-scroll-x"
          onClick={handleCloseEditModal}
        >
          <div
            className="permission-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="permission-edit-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="permission-modal-header">
              <h3 id="permission-edit-modal-title">관리자 수정</h3>

              <button
                type="button"
                className="permission-modal-close"
                onClick={handleCloseEditModal}
                aria-label="관리자 수정 팝업 닫기"
              >
                ×
              </button>
            </div>

            <div className="permission-modal-content">
              <ModalInputRow label="아이디">
                <span className="permission-modal-id">
                  {editingAdmin.loginId}
                </span>
              </ModalInputRow>

              <ModalInputRow label="이름">
                <input
                  type="text"
                  value={editingAdmin.name}
                  onChange={(e) =>
                    handleEditAdminChange("name", e.target.value)
                  }
                />
              </ModalInputRow>

              <ModalInputRow label="권한 그룹">
                <select
                  value={editingAdmin.roleName}
                  onChange={(e) =>
                    handleEditAdminChange("roleName", e.target.value)
                  }
                >
                  {permissionGroupOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </ModalInputRow>

              <ModalInputRow label="비밀번호">
                {passwordResetMessage ? (
                  <span className="permission-temporary-password">
                    {passwordResetMessage}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="permission-password-reset-btn"
                    onClick={handleResetPassword}
                    disabled={isSaving}
                  >
                    초기화
                  </button>
                )}
              </ModalInputRow>
            </div>

            <div className="permission-modal-buttons">
              <button
                type="button"
                className="permission-modal-cancel"
                onClick={handleCloseEditModal}
                disabled={isSaving}
              >
                취소
              </button>

              <button
                type="button"
                className="permission-modal-save"
                onClick={handleSaveEditedAdmin}
                disabled={isSaving}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PermissionInputRow({ label, children }) {
  return (
    <div className="permission-input-row">
      <div className="permission-row-label">
        <img src={flowerIcon} alt="" className="site-flower-icon" />
        <span>{label}</span>
      </div>

      <div className="permission-row-control">{children}</div>
    </div>
  );
}

function ModalInputRow({ label, children }) {
  return (
    <div className="permission-modal-input-row">
      <div className="permission-modal-label">
        <img src={flowerIcon} alt="" />
        <span>{label}</span>
      </div>

      <div className="permission-modal-control">{children}</div>
    </div>
  );
}

export default PermissionAdmin;